/**
 * Plain-language captions for a workflow (KWorkFlow) diff. A WF change touches both XML and JSON
 * metadata and the structural change (a renamed step, a changed step type, an added node) is what
 * matters — not the raw node tree. This produces captions like "Step 'Approve' renamed to 'Review'"
 * or "Step 'X' type changed from Backend Interaction to Screen Interaction", mirroring the
 * screen-design caption layer. Pure / framework-free for unit testing.
 */
import { BranchChangeCaption, BranchChangeKind } from "./branch-change-captions";

// EWFStepType (kuika-cl-model) → readable names.
const STEP_TYPE_NAMES: Record<number, string> = {
  1: "Screen Interaction",
  2: "Backend Interaction",
  3: "Subprocess",
  4: "Linked Process"
};

interface WfAction {
  id?: string;
  label?: string;
  Label?: string;
  actionDefinitionId?: string;
}

interface WfNode {
  id?: string;
  name?: string;
  Name?: string;
  displayName?: string;
  DisplayName?: string;
  stepType?: number;
  StepType?: number;
  actions?: WfAction[];
  Actions?: WfAction[];
}

interface WfNodeContainer {
  Steps?: WfNode[];
  steps?: WfNode[];
  Decisions?: WfNode[];
  decisions?: WfNode[];
  Gateways?: WfNode[];
  gateways?: WfNode[];
  Events?: WfNode[];
  events?: WfNode[];
}

interface WfParameter {
  id?: string;
  name?: string;
  Name?: string;
  dataType?: string;
  DataType?: string;
}

interface WfModel {
  Json?: unknown;
  json?: unknown;
  DisplayName?: string;
  displayName?: string;
  Description?: string;
  description?: string;
  Nodes?: WfNodeContainer | string;
  nodes?: WfNodeContainer | string;
  Parameters?: WfParameter[] | string;
  parameters?: WfParameter[] | string;
}

interface CategorizedNode {
  category: "Step" | "Decision" | "Gateway" | "Event";
  node: WfNode;
}

function isObject(value: unknown): value is WfModel {
  return typeof value === "object" && value !== null;
}

// Snapshot fields may be objects or stringified JSON (KScreenDesign.JSON-style). Coerce to object.
function parseObject<T>(value: unknown): T | null {
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      return typeof parsed === "object" && parsed !== null ? (parsed as T) : null;
    } catch {
      return null;
    }
  }
  return typeof value === "object" && value !== null ? (value as T) : null;
}

function parseArray<T>(value: unknown): T[] {
  const parsed: unknown = typeof value === "string" ? parseObject<unknown>(value) : value;
  return Array.isArray(parsed) ? (parsed as T[]) : [];
}

function nodeLabel(node: WfNode): string {
  const candidate: string | undefined = node.name ?? node.Name ?? node.displayName ?? node.DisplayName;
  if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  return node.id ? `#${node.id.slice(0, 6)}` : "unnamed";
}

function actionLabel(action: WfAction): string {
  const candidate: string | undefined = action.label ?? action.Label ?? action.actionDefinitionId;
  return typeof candidate === "string" && candidate.trim() ? candidate.trim() : "action";
}

function stepTypeName(node: WfNode): string | null {
  const t: number | undefined = node.stepType ?? node.StepType;
  if (t == null) return null;
  return STEP_TYPE_NAMES[t] ?? `Type ${t}`;
}

// The WF model lives either at the entity's top level or under a `Json`/`json` field.
function resolveModel(data: unknown): WfModel | null {
  if (!isObject(data)) return null;
  const inner: WfModel | null = parseObject<WfModel>(data.Json ?? data.json);
  if (inner && (inner.Nodes ?? inner.nodes ?? inner.Parameters ?? inner.parameters)) return inner;
  return data;
}

function collectNodes(model: WfModel | null): CategorizedNode[] {
  if (!model) return [];
  const container: WfNodeContainer = parseObject<WfNodeContainer>(model.Nodes ?? model.nodes) ?? {};
  const result: CategorizedNode[] = [];
  const push = (list: WfNode[] | undefined, category: CategorizedNode["category"]): void => {
    if (Array.isArray(list)) {
      for (const node of list) {
        if (isObject(node)) result.push({ category, node });
      }
    }
  };
  push(container.Steps ?? container.steps, "Step");
  push(container.Decisions ?? container.decisions, "Decision");
  push(container.Gateways ?? container.gateways, "Gateway");
  push(container.Events ?? container.events, "Event");
  return result;
}

function nodeMap(nodes: CategorizedNode[]): Map<string, CategorizedNode> {
  const map: Map<string, CategorizedNode> = new Map();
  for (const entry of nodes) {
    if (entry.node.id) map.set(entry.node.id, entry);
  }
  return map;
}

function paramLabel(param: WfParameter): string {
  const candidate: string | undefined = param.name ?? param.Name;
  return typeof candidate === "string" && candidate.trim() ? candidate.trim() : "parameter";
}

function pushCaption(
  out: BranchChangeCaption[],
  path: string,
  kind: BranchChangeKind,
  caption: string,
  componentId: string | null = null
): void {
  out.push({
    id: `${path}#${out.length}`,
    caption,
    kind,
    componentId,
    componentLabel: null,
    propertyLabel: null,
    before: null,
    after: null,
    path
  });
}

/** The diagram node ids touched by a diff, for highlighting in the BPMN visual diff. */
export function workflowChangedNodeIds(beforeData: unknown, afterData: unknown): string[] {
  const ids: Set<string> = new Set<string>();
  for (const c of generateWorkflowCaptions(beforeData, afterData)) {
    if (c.componentId) ids.add(c.componentId);
  }
  return [...ids];
}

/** Generates plain-language captions describing how a workflow changed between two snapshots. */
export function generateWorkflowCaptions(beforeData: unknown, afterData: unknown): BranchChangeCaption[] {
  const before: WfModel | null = resolveModel(beforeData);
  const after: WfModel | null = resolveModel(afterData);
  const captions: BranchChangeCaption[] = [];

  // Workflow display name.
  const beforeName: string = (before?.DisplayName ?? before?.displayName ?? "").trim();
  const afterName: string = (after?.DisplayName ?? after?.displayName ?? "").trim();
  if (beforeName && afterName && beforeName !== afterName) {
    pushCaption(captions, "workflow/name", "modified", `Workflow renamed from "${beforeName}" to "${afterName}"`);
  }

  // Nodes (steps / decisions / gateways / events), matched by id.
  const beforeNodes: Map<string, CategorizedNode> = nodeMap(collectNodes(before));
  const afterNodes: Map<string, CategorizedNode> = nodeMap(collectNodes(after));
  const allNodeIds: Set<string> = new Set<string>([...beforeNodes.keys(), ...afterNodes.keys()]);

  for (const id of allNodeIds) {
    const b: CategorizedNode | undefined = beforeNodes.get(id);
    const a: CategorizedNode | undefined = afterNodes.get(id);
    if (a && !b) {
      pushCaption(captions, `node/${id}`, "added", `${a.category} "${nodeLabel(a.node)}" added`, id);
      continue;
    }
    if (b && !a) {
      pushCaption(captions, `node/${id}`, "removed", `${b.category} "${nodeLabel(b.node)}" removed`, id);
      continue;
    }
    if (!a || !b) continue;
    const bLabel: string = nodeLabel(b.node);
    const aLabel: string = nodeLabel(a.node);
    if (bLabel !== aLabel) {
      pushCaption(captions, `node/${id}/name`, "modified", `${a.category} "${bLabel}" renamed to "${aLabel}"`, id);
    }
    const bType: string | null = stepTypeName(b.node);
    const aType: string | null = stepTypeName(a.node);
    if (bType !== null && aType !== null && bType !== aType) {
      pushCaption(
        captions,
        `node/${id}/type`,
        "modified",
        `${a.category} "${aLabel}" type changed from ${bType} to ${aType}`,
        id
      );
    }

    // Actions inside the step, matched by action id.
    const bActions: WfAction[] = Array.isArray(b.node.actions ?? b.node.Actions)
      ? (b.node.actions ?? b.node.Actions)!
      : [];
    const aActions: WfAction[] = Array.isArray(a.node.actions ?? a.node.Actions)
      ? (a.node.actions ?? a.node.Actions)!
      : [];
    const bActionMap: Map<string, WfAction> = new Map(bActions.filter((x) => x.id).map((x) => [x.id as string, x]));
    const aActionMap: Map<string, WfAction> = new Map(aActions.filter((x) => x.id).map((x) => [x.id as string, x]));
    for (const actionId of new Set<string>([...bActionMap.keys(), ...aActionMap.keys()])) {
      const ba: WfAction | undefined = bActionMap.get(actionId);
      const aa: WfAction | undefined = aActionMap.get(actionId);
      if (aa && !ba) {
        pushCaption(
          captions,
          `node/${id}/action/${actionId}`,
          "added",
          `${a.category} "${aLabel}": action "${actionLabel(aa)}" added`,
          id
        );
      } else if (ba && !aa) {
        pushCaption(
          captions,
          `node/${id}/action/${actionId}`,
          "removed",
          `${a.category} "${aLabel}": action "${actionLabel(ba)}" removed`,
          id
        );
      }
    }
  }

  // Parameters, matched by id.
  const beforeParams: WfParameter[] = parseArray<WfParameter>(before?.Parameters ?? before?.parameters);
  const afterParams: WfParameter[] = parseArray<WfParameter>(after?.Parameters ?? after?.parameters);
  const beforeParamMap: Map<string, WfParameter> = new Map(
    beforeParams.filter((p) => p.id).map((p) => [p.id as string, p])
  );
  const afterParamMap: Map<string, WfParameter> = new Map(
    afterParams.filter((p) => p.id).map((p) => [p.id as string, p])
  );
  const allParamIds: Set<string> = new Set<string>([...beforeParamMap.keys(), ...afterParamMap.keys()]);

  for (const id of allParamIds) {
    const b: WfParameter | undefined = beforeParamMap.get(id);
    const a: WfParameter | undefined = afterParamMap.get(id);
    if (a && !b) {
      pushCaption(captions, `param/${id}`, "added", `Parameter "${paramLabel(a)}" added`);
    } else if (b && !a) {
      pushCaption(captions, `param/${id}`, "removed", `Parameter "${paramLabel(b)}" removed`);
    } else if (a && b && paramLabel(b) !== paramLabel(a)) {
      pushCaption(
        captions,
        `param/${id}/name`,
        "modified",
        `Parameter "${paramLabel(b)}" renamed to "${paramLabel(a)}"`
      );
    }
  }

  return captions;
}
